import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button, type ButtonProps } from './Button';
import { useToast } from './Toast';

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  value: string;
  label?: string;
  copiedLabel?: string;
  toastTitle?: string;
}

/** Copies `value` to the clipboard and shows a toast. */
export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  toastTitle = 'Copied to clipboard',
  variant = 'secondary',
  size = 'sm',
  disabled,
  ...rest
}: CopyButtonProps) {
  const { notify } = useToast();
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notify({ tone: 'success', title: toastTitle });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      notify({ tone: 'error', title: 'Could not copy', description: 'Clipboard access was denied.' });
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled || !value}
      onClick={() => void onCopy()}
      leftIcon={copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      {...rest}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}
